import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useToken,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery } from 'api';
import { PostInvoiceTransactionAccountRequest } from 'api/bindings/PostInvoiceTransactionAccountRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import Decimal from 'decimal.js';
import {
  AppInput,
  AutoComplete,
  CurrencyLabel,
  FormButtonGroup,
  MultiBar,
} from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface Option {
  _id: number;
  name: string;
  commitmentId: string;
  commitmentTypeId: number;
  subAccountId: string;
  subAccount: SubAccountResponse;
  total: string;
  pending: string;
  pendingInvoiced: string;
  totalInvoiced: string;
}
interface Form {
  _id: number;
  amount: string;
}

export interface InvoiceFormRevisionAccountDrawerProps
  extends Omit<DrawerProps, 'children' | 'onSubmit'> {
  afeId: string;
  row: PostInvoiceTransactionAccountRequest;
  onSubmit: (row: PostInvoiceTransactionAccountRequest) => void;
}

export const InvoiceFormRevisionAccountDrawer = (
  props: InvoiceFormRevisionAccountDrawerProps
) => {
  const { afeId, row, ...others } = props;

  const { data: afe } = useFetchAfeDetailsQuery(afeId, { skip: !afeId });
  const options = useMemo(() => {
    let id = 0;
    return afe?.commitments.reduce((sum, n) => {
      return n.accounts.reduce((sum2, n2) => {
        return [
          ...sum2,
          {
            _id: id++,
            name: `${n.name} ${n2.subAccount.fullSubAccountName}`,
            ...n2,
          } as Option,
        ];
      }, sum);
    }, [] as Option[]);
  }, [afe?.commitments]);

  const preSelectedOptionId = useMemo(
    () =>
      row.commitmentId &&
      row.subAccountId &&
      options?.find(
        (x) =>
          x.commitmentId === row.commitmentId &&
          x.commitmentTypeId === row.commitmentTypeId &&
          row.subAccountId === x.subAccountId
      )?._id,
    [options, row.commitmentId, row.commitmentTypeId, row.subAccountId]
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      _id: preSelectedOptionId as number,
      amount: row.amount,
    },
  });

  const optionId = form.watch('_id');
  const amount = form.watch('amount');

  const selectedOption = useMemo(
    () => options?.find((x) => x._id === optionId),
    [optionId, options]
  );

  const eacLineItem = useMemo(
    () =>
      afe?.eacLineItems.find(
        (x) => x.subAccountId === selectedOption?.subAccountId
      ),
    [afe?.eacLineItems, selectedOption?.subAccountId]
  );

  const remainder = useMemo(() => {
    return new Decimal(selectedOption?.total ?? 0)
      .sub(amount)
      .sub(selectedOption?.pendingInvoiced ?? 0)
      .sub(selectedOption?.totalInvoiced ?? 0)
      .toString();
  }, [
    amount,
    selectedOption?.pendingInvoiced,
    selectedOption?.total,
    selectedOption?.totalInvoiced,
  ]);

  const onSubmit = useCallback(
    (form: Form) => {
      if (!selectedOption) return;
      props.onSubmit({
        ...row,
        commitmentTypeId: selectedOption.commitmentTypeId,
        commitmentId: selectedOption.commitmentId,
        subAccountId: selectedOption.subAccountId,
        amount: form.amount,
      });
      props.onClose();
    },
    [props, row, selectedOption]
  );

  return (
    <FormProvider {...form}>
      <Drawer {...others}>
        <DrawerCloseButton />
        <DrawerHeader>
          <Heading size="xs">Invoice Account</Heading>
        </DrawerHeader>
        <DrawerContent>
          <form>
            <DrawerBody>
              <Stack spacing={5}>
                <FormControl>
                  <FormLabel htmlFor="id">AFE Account</FormLabel>
                  <Controller
                    control={form.control}
                    name="_id"
                    rules={{ min: 0 }}
                    render={(p) => (
                      <AutoComplete
                        id="id"
                        items={options}
                        valueKey={(x) => x._id}
                        textKey={(x) => x.name}
                        {...p.field}
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Amount</FormLabel>
                  <Controller
                    control={form.control}
                    name="amount"
                    rules={{ min: 0 }}
                    render={(p) => (
                      <AppInput mode="currency" id="amount" {...p.field} />
                    )}
                  />
                </FormControl>
                <Flex w="100%">
                  <MultiBar
                    max={[selectedOption?.total]}
                    values={[
                      selectedOption?.totalInvoiced,
                      selectedOption?.pendingInvoiced,
                      amount,
                    ]}
                  />
                </Flex>
                <SimpleGrid columns={2} gap={[2, 4]}>
                  <Text as="b">Budget:</Text>
                  <CurrencyLabel value={eacLineItem?.totalBudget} />
                  <Text as="b">Contingency:</Text>
                  <CurrencyLabel value={eacLineItem?.totalContingency} />
                  <Text as="b">Total:</Text>
                  <CurrencyLabel
                    value={[
                      eacLineItem?.totalBudget,
                      eacLineItem?.totalContingency,
                    ]}
                  />
                  <Divider />
                  <Divider />
                  <Text as="b">Commitment Total:</Text>
                  <CurrencyLabel value={selectedOption?.total} />
                  <Text as="b">Invoiced:</Text>
                  <CurrencyLabel value={selectedOption?.totalInvoiced} />
                  <Text as="b">Invoiced (Pending):</Text>
                  <CurrencyLabel value={selectedOption?.pendingInvoiced} />
                  <Text as="b">Amount:</Text>
                  <CurrencyLabel value={amount} />
                  <Text as="b">Remaining to Invoice:</Text>
                  <CurrencyLabel value={remainder} />
                </SimpleGrid>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                submitCaption="Save Row"
                onCancel={props.onClose}
                onConfirm={form.handleSubmit(onSubmit)}
              />
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
    </FormProvider>
  );
};
